import { useState, useRef, useEffect } from 'react';
import {
  CheckOutlined,
  ExclamationOutlined,
  LoadingOutlined,
  HistoryOutlined,
} from '@ant-design/icons';
import { Tooltip, Drawer, Timeline, Modal } from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '@frontegg/react';
import { analytics } from 'src/services';

const VersionHistoryItem = ({
  summary,
  onRollback,
  isRollingBack,
  onClose,
}) => {
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);
  const quillRef = useRef(null);
  const { user } = useAuth();
  const getSourceIcon = (source) => {
    return source === 'user' ? '👤' : '🤖';
  };

  const parsedSummary = summary.summary_json
    .replaceAll('\n', '<br>')
    .replaceAll('\\n', '<br>');

  useEffect(() => {
    if (isViewModalOpen && quillRef.current) {
      const editor = quillRef.current.getEditor();
      if (editor) {
        setIsEditorReady(true);
      }
    } else {
      setIsEditorReady(false);
    }
  }, [isViewModalOpen, summary]);

  const localizedCreatedAt = moment
    .utc(summary.created_at)
    .local()
    .format('MMMM D, YYYY h:mm A');

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <span>{getSourceIcon(summary.source)}</span>
        <span className="font-medium">
          {summary.source === 'user'
            ? summary.created_by || 'User Edited'
            : 'AI Generated'}
        </span>
      </div>
      <div className="text-gray-500 text-sm">{localizedCreatedAt}</div>

      <div className="flex gap-4 mt-2">
        <button
          onClick={() => {
            analytics.track('Clicked On View Version', { email: user?.email });
            setIsViewModalOpen(true);
          }}
          className="text-left text-blue-600 hover:text-blue-800"
        >
          View this version
        </button>
        <button
          onClick={() => {
            analytics.track('Clicked On Restore Version', {
              email: user?.email,
            });
            onRollback(summary);
            onClose();
          }}
          disabled={isRollingBack}
          className="text-left text-blue-600 hover:text-blue-800"
        >
          Restore this version
        </button>
      </div>

      <Modal
        title={`${localizedCreatedAt}`}
        open={isViewModalOpen}
        onCancel={() => {
          setIsViewModalOpen(false);
          setIsEditorReady(false);
        }}
        footer={null}
        width={800}
      >
        <div className="mt-4">
          <div className="text-gray-500 text-sm mb-2">
            <strong>Template:</strong>{' '}
            {summary.template_name || summary.template_overrides_name || 'N/A'}
          </div>
          <div className="text-gray-500 text-sm mb-2">
            <strong>Verbosity:</strong> {summary.verbosity || 'N/A'}
          </div>
          <div className="text-gray-500 text-sm mb-4">
            <strong>Magic edit instructions:</strong>{' '}
            {summary.custom_instructions || 'None'}
          </div>
        </div>
        <div className="quill-view-only">
          {!isEditorReady && (
            <div className="flex justify-center py-4">
              <LoadingOutlined className="text-2xl text-gray-400" />
            </div>
          )}
          <div style={{ visibility: isEditorReady ? 'visible' : 'hidden' }}>
            <ReactQuill
              ref={quillRef}
              value={parsedSummary}
              className="current bg-white ql-toolbar font-sans pt-4"
              readOnly={true}
              theme="snow"
              modules={{
                toolbar: false,
                clipboard: {
                  matchVisual: false,
                },
              }}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const SummaryUpdatedStatus = ({
  isSaving,
  lastSaved,
  isRollingBack,
  onRollback,
  encounterStatus,
  encounterPhase,
  isSummaryOutdated,
}) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { summaryList } = useSelector((state) => state?.summaryState);
  const { user } = useAuth();

  const onVersionHistoryClick = () => {
    setIsDrawerOpen(true);
    analytics.track('Clicked On Show Version History', { email: user?.email });
  };

  const onVersionHistoryClose = () => {
    setIsDrawerOpen(false);
    analytics.track('Closed Show Version History', { email: user?.email });
  };

  if (encounterStatus === 'summary_inprogress') {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <LoadingOutlined className="text-gray-400" />
        <span>Generating...</span>
      </div>
    );
  }

  return (
    <>
      {(encounterStatus === 'completed' || encounterPhase === 'dictate') &&
        (isSummaryOutdated ? (
          <div className="flex justify-center text-xs text-orange-400">
            <ExclamationOutlined
              style={{ color: 'inherit' }}
              className="mr-1"
            />
            A newer summary is available. Reload the page to make edits.
          </div>
        ) : (
          <div className="flex items-center gap-2 text-xs text-gray-500">
            {isSaving ? (
              <>
                <LoadingOutlined className="text-gray-400" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <CheckOutlined className="text-gray-400" />
                <span>Last saved {moment(lastSaved).fromNow()}</span>

                {encounterStatus === 'completed' && (
                  <Tooltip title="View version history">
                    <a
                      onClick={onVersionHistoryClick}
                      className="flex items-center justify-center p-1 hover:bg-gray-100 rounded-full cursor-pointer underline"
                    >
                      <HistoryOutlined className="mr-1" />
                      {summaryList?.length} Version(s)
                    </a>
                  </Tooltip>
                )}
              </>
            )}
          </div>
        ))}

      <Drawer
        title="Version History"
        placement="right"
        onClose={onVersionHistoryClose}
        open={isDrawerOpen}
        width={400}
      >
        <Timeline
          items={summaryList?.map((summary) => ({
            children: (
              <VersionHistoryItem
                summary={summary}
                onRollback={onRollback}
                isRollingBack={isRollingBack}
                onClose={onVersionHistoryClose}
              />
            ),
          }))}
        />
      </Drawer>
    </>
  );
};
